/* ==========================================================================
   Bundle deals — in-theme volume-break widget (replaces Kaching Bundles).

   What this file does:
     - presents the quantity tiers and keeps their prices in sync with the
       variant the shopper has selected;
     - for multi-unit tiers it renders one variant picker per unit, so the
       shopper can mix colours/sizes inside a bundle;
     - on Add to cart it posts every chosen unit to /cart/add.js in a single
       request and then hands control back to the theme's cart drawer.

   What this file does NOT do: apply the discount. Money is discounted by a
   Shopify AUTOMATIC DISCOUNT (e.g. "2+ items -> 10% off"). The percentages
   configured on the block are display only and must match those discounts.
   ========================================================================== */

(function () {
  'use strict';

  var SELECTED = 'is-selected';

  function formatMoney(cents) {
    if (window.theme && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, theme.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  class BundleDeals extends HTMLElement {
    connectedCallback() {
      var configEl = this.querySelector('[data-bundle-config]');
      if (!configEl) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        return;
      }

      this.product = this.config.product;
      if (!this.product || !this.product.variants.length) return;

      this.form = document.getElementById(this.dataset.formId);
      this.masterSelect = this.form && this.form.querySelector('[data-product-select]');
      this.tiers = Array.prototype.slice.call(this.querySelectorAll('[data-tier]'));
      this.errorEl = this.querySelector('[data-bd-error]');

      this.currentVariant = this.getCurrentVariant();
      this.selectedTier = this.tiers.find(function (t) {
        return t.classList.contains(SELECTED);
      }) || this.tiers[0];

      this.buildUnitPickers();
      this.bindEvents();
      this.selectTier(this.selectedTier, true);
    }

    /* ---------------------------------------------------------------- data */

    getCurrentVariant() {
      var id = this.masterSelect && Number(this.masterSelect.value);
      return this.findVariantById(id) || this.product.variants.find(function (v) {
        return v.available;
      }) || this.product.variants[0];
    }

    findVariantById(id) {
      if (!id) return null;
      return this.product.variants.find(function (v) { return v.id === id; }) || null;
    }

    findVariantByOptions(values) {
      return this.product.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === values[i]; });
      }) || null;
    }

    /* ------------------------------------------------------------ rendering */

    buildUnitPickers() {
      if (!this.config.differentVariants) return;
      if (this.product.options.length === 0) return;

      var self = this;

      this.tiers.forEach(function (tier) {
        var qty = Number(tier.dataset.qty);
        var holder = tier.querySelector('[data-bd-units]');
        if (!holder || qty < 2) return;

        holder.classList.add('is-enabled');
        holder.innerHTML = '';

        for (var i = 0; i < qty; i++) {
          holder.appendChild(self.buildUnitRow(i));
        }
      });
    }

    buildUnitRow(index) {
      var self = this;
      var row = document.createElement('div');
      row.className = 'bundle-deals__unit';
      row.dataset.unit = String(index);

      var label = document.createElement('span');
      label.className = 'bundle-deals__unit-label';
      label.textContent = this.config.strings.item.replace('[number]', index + 1);
      row.appendChild(label);

      this.product.options.forEach(function (option, optionIndex) {
        var select = document.createElement('select');
        select.className = 'bundle-deals__select';
        select.dataset.optionIndex = String(optionIndex);
        select.setAttribute('aria-label', option.name);

        option.values.forEach(function (value) {
          var opt = document.createElement('option');
          opt.value = value;
          opt.textContent = value;
          if (self.currentVariant.options[optionIndex] === value) opt.selected = true;
          select.appendChild(opt);
        });

        select.addEventListener('change', function () {
          self.onUnitChange(row);
        });

        row.appendChild(select);
      });

      return row;
    }

    /* Values of a unit row, as an array in option order. */
    unitValues(row) {
      return Array.prototype.map.call(
        row.querySelectorAll('.bundle-deals__select'),
        function (s) { return s.value; }
      );
    }

    unitVariant(row) {
      return this.findVariantByOptions(this.unitValues(row));
    }

    /* Grey out combinations that do not exist or are sold out. */
    refreshUnitAvailability(row) {
      var self = this;
      var values = this.unitValues(row);

      row.querySelectorAll('.bundle-deals__select').forEach(function (select) {
        var idx = Number(select.dataset.optionIndex);

        Array.prototype.forEach.call(select.options, function (opt) {
          var probe = values.slice();
          probe[idx] = opt.value;
          var variant = self.findVariantByOptions(probe);
          var ok = !!(variant && variant.available);
          opt.disabled = !ok;
          opt.textContent = ok ? opt.value : opt.value + ' — ' + self.config.strings.unavailable;
        });

        var variant = self.unitVariant(row);
        select.dataset.unavailable = variant && variant.available ? 'false' : 'true';
      });
    }

    /* ------------------------------------------------------------- pricing */

    /* Variants making up a tier: per-unit choices when available,
       otherwise the page's current variant repeated. */
    tierVariants(tier) {
      var self = this;
      var qty = Number(tier.dataset.qty);
      var rows = tier.querySelectorAll('[data-unit]');

      if (!rows.length) {
        return new Array(qty).fill(this.currentVariant);
      }

      return Array.prototype.map.call(rows, function (row) {
        return self.unitVariant(row) || self.currentVariant;
      });
    }

    updatePrices() {
      var self = this;

      this.tiers.forEach(function (tier) {
        var discount = Number(tier.dataset.discount) || 0;
        var variants = self.tierVariants(tier);

        var full = 0;   // compare-at total, for the struck-through price
        var gross = 0;  // today's price before the bundle discount
        var net = 0;    // what they actually pay

        variants.forEach(function (variant) {
          var price = variant.price;
          var compare = self.config.useCompareAt && variant.compare_at_price > price
            ? variant.compare_at_price
            : price;
          full += compare;
          gross += price;
        });

        net = Math.round(gross * (100 - discount) / 100);

        var priceEl = tier.querySelector('[data-bd-price]');
        var compareEl = tier.querySelector('[data-bd-compare]');
        var labelEl = tier.querySelector('[data-bd-label]');

        if (priceEl) priceEl.textContent = formatMoney(net);

        if (compareEl) {
          var showCompare = full > net;
          compareEl.textContent = showCompare ? formatMoney(full) : '';
          compareEl.style.display = showCompare ? '' : 'none';
        }

        if (labelEl) {
          // 'extra' counts only what the bundle discount saves, so a single
          // item shows nothing. 'total' also counts the compare-at saving,
          // which a shopper gets whether they bundle or not.
          var saved = self.config.savingsBasis === 'total' ? full - net : gross - net;
          labelEl.textContent = labelEl.dataset.template.replace(
            '[saved_total]',
            formatMoney(saved > 0 ? saved : 0)
          );
          labelEl.style.display = saved > 0 ? '' : 'none';
        }
      });
    }

    /* -------------------------------------------------------------- events */

    bindEvents() {
      var self = this;

      this.tiers.forEach(function (tier) {
        tier.addEventListener('click', function (event) {
          if (event.target.closest('[data-bd-units]')) return; // don't steal picker clicks
          self.selectTier(tier);
        });
      });

      document.addEventListener('variant:change', function (event) {
        var variant = event.detail && event.detail.variant;
        if (!variant) return;
        var full = self.findVariantById(variant.id);
        if (!full) return;
        self.currentVariant = full;
        self.syncFirstUnit();
        self.updatePrices();
      });

      // Add to cart is intercepted on the CLICK, in the capture phase, on the
      // document. Cart drawer apps (UpCart here) bind their own click handler
      // to the button and serialise the form themselves, so a submit listener
      // never runs. Capturing on the document gets us in ahead of them.
      document.addEventListener('click', function (event) {
        var button = event.target.closest && event.target.closest('[data-add-to-cart]');
        if (!button) return;
        if (self.form && !self.form.contains(button)) return;
        if (!self.wantsBundle()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        self.submitBundle();
      }, true);

      // Fallback for setups where the form submits normally (e.g. Enter key).
      if (this.form) {
        this.form.addEventListener('submit', function (event) {
          if (!self.wantsBundle()) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          self.submitBundle();
        }, true);
      }
    }

    /* Keep unit 1 in step with the theme's own variant picker. */
    syncFirstUnit() {
      var self = this;

      this.tiers.forEach(function (tier) {
        var row = tier.querySelector('[data-unit="0"]');
        if (!row) return;

        row.querySelectorAll('.bundle-deals__select').forEach(function (select) {
          var idx = Number(select.dataset.optionIndex);
          select.value = self.currentVariant.options[idx];
        });

        self.refreshUnitAvailability(row);
      });
    }

    onUnitChange(row) {
      this.refreshUnitAvailability(row);

      // Unit 1 drives the theme's picker so the gallery and price follow along.
      if (row.dataset.unit === '0' && !this.config.disableSync) {
        var variant = this.unitVariant(row);
        if (variant) this.pushVariantToTheme(variant);
      }

      this.clearError();
      this.updatePrices();
    }

    pushVariantToTheme(variant) {
      if (!this.form) return;
      var self = this;

      this.product.options.forEach(function (option, i) {
        var selector = '[name="options[' + CSS.escape(option.name) + ']"]';
        var inputs = self.form.querySelectorAll(selector);

        inputs.forEach(function (input) {
          if (input.tagName === 'SELECT') {
            if (input.value === variant.options[i]) return;
            input.value = variant.options[i];
            input.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (input.type === 'radio') {
            if (input.value !== variant.options[i] || input.checked) return;
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    selectTier(tier, silent) {
      var self = this;

      this.tiers.forEach(function (t) {
        var on = t === tier;
        t.classList.toggle(SELECTED, on);
        var radio = t.querySelector('input[type="radio"]');
        if (radio) radio.checked = on;
      });

      this.selectedTier = tier;
      this.clearError();

      tier.querySelectorAll('[data-unit]').forEach(function (row) {
        self.refreshUnitAvailability(row);
      });

      this.updatePrices();

      if (!silent) {
        this.dispatchEvent(new CustomEvent('bundle:tierchange', {
          bubbles: true,
          detail: { quantity: Number(tier.dataset.qty), discount: Number(tier.dataset.discount) }
        }));
      }
    }

    /* ---------------------------------------------------------------- cart */

    /* A single-item tier is left to the theme and the cart drawer app. */
    wantsBundle() {
      var tier = this.selectedTier;
      return !!tier && Number(tier.dataset.qty) > 1 && !this.adding;
    }

    submitBundle() {
      var tier = this.selectedTier;
      if (!tier) return;

      var variants = this.tierVariants(tier);
      var unavailable = variants.some(function (v) { return !v || !v.available; });

      if (unavailable) {
        this.showError(this.config.strings.unavailableSelection);
        return;
      }

      // Collapse repeated variants into single lines with a quantity.
      var lines = [];
      variants.forEach(function (variant) {
        var existing = lines.find(function (l) { return l.id === variant.id; });
        if (existing) existing.quantity += 1;
        else lines.push({ id: variant.id, quantity: 1 });
      });

      this.addToCart(lines);
    }

    addToCart(items) {
      var self = this;
      var button = this.form && this.form.querySelector('[data-add-to-cart]');
      if (button) button.classList.add('btn--loading');
      this.adding = true;

      var route = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';

      fetch(route, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) { return response.json(); })
        .then(function (data) {
          if (button) button.classList.remove('btn--loading');
          self.adding = false;

          if (data.status === 422 || data.status === 404) {
            self.showError(data.description || data.message || self.config.strings.error);
            return;
          }

          var added = (data.items && data.items[0]) || data;

          // Hand back to the theme (and any cart drawer app listening on these).
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: added, addToCartBtn: button }
          }));
          document.dispatchEvent(new CustomEvent('cart:build'));
        })
        .catch(function () {
          if (button) button.classList.remove('btn--loading');
          self.adding = false;
          self.showError(self.config.strings.error);
        });
    }

    /* --------------------------------------------------------------- errors */

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.classList.add('is-visible');
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.classList.remove('is-visible');
    }
  }

  if (!customElements.get('bundle-deals')) {
    customElements.define('bundle-deals', BundleDeals);
  }
})();

/* ==========================================================================
   Bundle deals — countdown timer bar.

   Three modes, matching the Kaching settings they replace:
     midnight  - counts down to the shopper's own local midnight, then rolls
                 over to the next day (never expires)
     duration  - a fixed number of minutes, held for the browsing session
     date      - counts down to one fixed date, then hides itself
   ========================================================================== */

(function () {
  'use strict';

  function two(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function formatLeft(ms) {
    var total = Math.max(0, Math.floor(ms / 1000));
    var h = Math.floor(total / 3600);
    var m = Math.floor((total % 3600) / 60);
    var s = total % 60;
    return h > 0 ? two(h) + ':' + two(m) + ':' + two(s) : two(m) + ':' + two(s);
  }

  function nextMidnight() {
    var d = new Date();
    d.setHours(24, 0, 0, 0);
    return d.getTime();
  }

  function targetFor(el) {
    var type = el.dataset.timerType;

    if (type === 'duration') {
      var minutes = Number(el.dataset.timerMinutes) || 15;
      var key = 'bundleDealsTimer';
      var stored = null;
      try {
        stored = window.sessionStorage.getItem(key);
      } catch (e) {
        stored = null;
      }
      var end = stored ? Number(stored) : Date.now() + minutes * 60000;
      if (!stored) {
        try {
          window.sessionStorage.setItem(key, String(end));
        } catch (e) {
          /* private mode: fall back to a per-page countdown */
        }
      }
      return end;
    }

    if (type === 'date') {
      var parsed = Date.parse(el.dataset.timerEnd);
      return isNaN(parsed) ? 0 : parsed;
    }

    return nextMidnight();
  }

  function start(el) {
    var template = el.dataset.timerTemplate || '';
    var target = targetFor(el);

    function tick() {
      var left = target - Date.now();

      if (left <= 0) {
        if (el.dataset.timerType === 'midnight') {
          target = nextMidnight();
          left = target - Date.now();
        } else {
          el.hidden = true;
          window.clearInterval(handle);
          return;
        }
      }

      var value = '<span class="bundle-deals__timer-value">' + formatLeft(left) + '</span>';
      el.innerHTML = template.split('[timer]').join(value);
      el.hidden = false;
    }

    var handle = window.setInterval(tick, 1000);
    tick();
  }

  function init() {
    var bars = document.querySelectorAll('[data-bd-timer]');
    Array.prototype.forEach.call(bars, function (el) {
      if (el.dataset.timerReady) return;
      el.dataset.timerReady = '1';
      start(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
